import { useState } from "react";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { Switch } from "./ui/switch";
import { SectionCard } from "./identityForm";
import { notificationsSummary } from "../lib/settingsSummary";

function Row({ title, desc, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div><div className="font-semibold text-slate-900">{title}</div><div className="text-sm text-slate-500">{desc}</div></div>
      {children}
    </div>
  );
}

/**
 * "Notifications" — moved out of `pages/Settings.jsx` unchanged by
 * #456/#459, then given a third channel and a fold.
 *
 * IT STILL PERSISTS NOTHING, AND THAT IS NOT NEW. Every switch here is
 * local state and "Save preferences" is `toast.success("Preferences
 * saved")` with no request behind it — the exact defect
 * `SendFeedbackCard.jsx` names as the regression IT must never repeat
 * (#139), and the reason `responsive.spec.js` picked this button as its
 * inert toast probe. #456/#459 asked for a mobile-phone CHANNEL alongside
 * the two that were already here, so it is wired the same way they are.
 * Making the three of them real is a separate slice with a schema change
 * behind it, and dressing this one up as persistent would hide that.
 *
 * MOBILE-PHONE ALERTS HAVE NOWHERE TO SEND TO YET. #456 also asks for a
 * "Mobile phone" FIELD on the player's account; there is no column for a
 * phone number anywhere in `api/src/db/schema.ts` (the emergency contact's
 * number is somebody else's, on `player_health`, and is not a channel), so
 * that field waits on the migration lane. The switch is honest about the
 * dependency in its own description rather than implying a number is on
 * file.
 *
 * THE FOLD arrives closed, like the other four settings sections, and its
 * header state is how many channels are on — "2 on" — which is the one
 * fact a folded notification card has to be able to answer.
 */
export default function NotificationsCard({ appearance } = {}) {
  const [notif, setNotif] = useState(true);
  const [emailAlerts, setEmailAlerts] = useState(false);
  const [mobileAlerts, setMobileAlerts] = useState(false);

  return (
    <SectionCard
      icon={Bell}
      title="Notifications"
      desc="Which reminders reach you, and where."
      collapsible
      defaultOpen={false}
      summary={notificationsSummary([notif, emailAlerts, mobileAlerts])}
      toggleTestid="settings-notifications-toggle"
      usageItem="notifications"
      testid="settings-notifications-card"
      appearance={appearance}
    >
      <Row title="Training reminders" desc="Get notified before scheduled sessions.">
        <Switch checked={notif} onCheckedChange={setNotif} data-testid="settings-notif" />
      </Row>
      <Separator />
      <Row title="Email alerts" desc="Receive weekly performance digest by email.">
        <Switch checked={emailAlerts} onCheckedChange={setEmailAlerts} data-testid="settings-email" />
      </Row>
      <Separator />
      <Row
        title="Mobile phone alerts"
        desc="Send the same reminders by text message. Needs a mobile number on your account, which we can't store yet."
      >
        <Switch checked={mobileAlerts} onCheckedChange={setMobileAlerts} data-testid="settings-notif-mobile" />
      </Row>
      <Button onClick={() => toast.success("Preferences saved")} style={{ background: "#0B2E59" }} data-testid="settings-save">Save preferences</Button>
    </SectionCard>
  );
}
